<template>
  <div class="bug-reports-component">
    <div class="reports-section">
      <div class="section-header">
        <h2>Bug Reports</h2>
        <div class="header-controls">
          <div class="search-container">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search reports..."
              class="search-input"
            />
          </div>
          <button :disabled="loading" class="refresh-btn" @click="loadReports">
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>

      <div v-if="loading && reports.length === 0" class="loading">Loading bug reports...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="filteredReports.length === 0" class="empty">No bug reports found.</div>
      <div v-else class="table-container">
        <table class="reports-table">
          <thead>
            <tr>
              <th class="date-column">Date</th>
              <th>Name</th>
              <th class="type-column">Type</th>
              <th class="desc-column">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="report in filteredReports"
              :key="report.id"
              class="report-row"
              @click="selectedReport = report"
            >
              <td class="date-column">{{ formatDate(report.createdAt) }}</td>
              <td class="name-cell">{{ report.name }}</td>
              <td class="type-column">
                <span class="type-pill">{{ report.type }}</span>
              </td>
              <td class="desc-column">
                <span class="desc-text">{{ truncate(report.text) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <BugReportDetail
      v-if="selectedReport"
      :report="selectedReport"
      @close="selectedReport = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { client, parseErrorMessage, type BugReport } from '../client.js';
import BugReportDetail from './BugReportDetail.vue';

const reports = ref<BugReport[]>([]);
const searchQuery = ref('');
const selectedReport = ref<BugReport | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

const filteredReports = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return reports.value;
  return reports.value.filter((report) =>
    [report.name, report.type, report.text, report.userId ?? '', JSON.stringify(report.metadata)]
      .join(' ')
      .toLowerCase()
      .includes(query),
  );
});

const formatDate = (date: Date) => new Date(date).toLocaleString();

const truncate = (text: string, max = 120) =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

const loadReports = async () => {
  if (!client.value) return;
  loading.value = true;
  error.value = null;
  try {
    reports.value = await client.value.getBugReports();
  } catch (err: any) {
    error.value = parseErrorMessage(err);
    console.error('Error loading bug reports:', err);
  } finally {
    loading.value = false;
  }
};

onMounted(loadReports);
</script>

<style scoped>
.bug-reports-component {
  width: 100%;
}

.reports-section {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.section-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h2 {
  margin: 0;
  color: #1f2937;
}

.header-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 240px;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.refresh-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.refresh-btn:hover:not(:disabled) {
  background: #2563eb;
}

.refresh-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.table-container {
  overflow-x: auto;
}

.reports-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.report-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.report-row:hover {
  background-color: #f3f4f6;
}

.reports-table th,
.reports-table td {
  padding: 0.75rem 0.5rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.reports-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.reports-table td {
  color: #1f2937;
  vertical-align: top;
  font-size: 0.875rem;
}

.date-column {
  width: 170px;
  white-space: nowrap;
}

.type-column {
  width: 160px;
}

.name-cell {
  font-weight: 600;
  word-break: break-word;
}

.type-pill {
  display: inline-block;
  background: #e0e7ff;
  color: #3730a3;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.desc-text {
  display: block;
  color: #4b5563;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loading,
.empty {
  color: #6b7280;
  font-style: italic;
  text-align: center;
  padding: 2rem;
}

.error {
  color: #991b1b;
  background: #fee2e2;
  padding: 1rem;
  margin: 1.5rem;
  border-radius: 0.375rem;
  text-align: center;
}

@media (max-width: 768px) {
  .section-header {
    padding: 0.75rem;
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }

  .header-controls {
    flex-direction: column;
    gap: 0.75rem;
  }

  .search-input {
    width: 100%;
  }

  /* Hide the description column on mobile; tap a row for full detail. */
  .desc-column {
    display: none;
  }

  .date-column {
    width: auto;
    white-space: normal;
  }
}
</style>
